import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  LinearProgress,
} from '@mui/joy';
import {
  CheckCircle as VerifiedIcon,
  Warning as WarningIcon,
  ShieldCheck as SecurityIcon,
  ArrowsClockwise as RefreshIcon,
  Timer as TimerIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface Domain {
  id: string;
  host: string;
  type: 'primary' | 'custom';
  verified: boolean;
  ssl_status: string;
}

interface DomainVerificationProps {
  open: boolean;
  onClose: () => void;
  domain: Domain;
  siteId: number;
}

export const DomainVerification: React.FC<DomainVerificationProps> = ({
  open,
  onClose,
  domain,
  siteId,
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [verificationMessage, setVerificationMessage] = useState('');
  const queryClient = useQueryClient();

  // Fetch verification details
  const { data: verificationData, refetch } = useQuery({
    queryKey: ['domain-verification', siteId, domain.id],
    queryFn: async () => {
      const response = await api.get(`/api/cms/sites/${siteId}/domains/${domain.id}/verify/`);
      return response.data || response;
    },
    enabled: open,
  });

  // Verify domain mutation
  const verifyDomainMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/cms/sites/${siteId}/domains/${domain.id}/verify/`, {});
    },
    onSuccess: (response) => {
      const data = (response.data || response) as any;
      if (data.verified) {
        setVerificationStatus('success');
        setVerificationMessage(data.message || 'Domain verified successfully');
        queryClient.invalidateQueries({ queryKey: ['cms-domains', siteId] });
        
        // If platform managed, show additional info
        if (data.platform_managed) {
          setTimeout(() => {
            setVerificationMessage(prev => 
              prev + '\n\nNote: SSL certificate provisioning is handled automatically by the platform.'
            );
          }, 1000);
        }
      } else {
        setVerificationStatus('error');
        setVerificationMessage(data.message || 'Verification in progress');
      }
      setIsVerifying(false);
    },
    onError: (error: any) => {
      setVerificationStatus('error');
      setVerificationMessage(error.response?.data?.message || 'Verification failed');
      setIsVerifying(false);
    },
  });

  const handleVerify = () => {
    setIsVerifying(true);
    setVerificationStatus('pending');
    verifyDomainMutation.mutate();
  };

  const handleRetry = () => {
    setVerificationStatus('pending');
    setVerificationMessage('');
    refetch();
  };

  // Auto-refresh verification status
  useEffect(() => {
    if (open && !domain.verified) {
      const interval = setInterval(() => {
        refetch();
      }, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, [open, domain.verified, refetch]);

  const getStatusColor = () => {
    switch (verificationStatus) {
      case 'success': return 'success';
      case 'error': return 'danger';
      default: return 'warning';
    }
  };

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'success': return <VerifiedIcon color="var(--joy-palette-success-500)" />;
      case 'error': return <WarningIcon color="var(--joy-palette-danger-500)" />;
      default: return <TimerIcon color="var(--joy-palette-warning-500)" />;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Verify Domain: {domain.host}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {domain.verified ? (
            // Already verified
            <Card variant="soft" color="success">
              <CardContent sx={{ textAlign: 'center' }}>
                <VerifiedIcon size={48} color="var(--joy-palette-success-500)" style={{ marginBottom: 16 }} />
                <Typography level="h3" sx={{ mb: 1 }}>
                  Domain Verified!
                </Typography>
                <Typography level="body-md" sx={{ mb: 2 }}>
                  Your domain is verified and SSL certificate is active.
                </Typography>
                <Button
                  variant="solid"
                  color="success"
                  onClick={() => window.open(`https://${domain.host}`, '_blank')}
                >
                  Visit Your Site
                </Button>
              </CardContent>
            </Card>
          ) : (
            // Verification process
            <>
              <Alert sx={{ mb: 3 }}>
                <Typography level="body-sm">
                  To verify ownership of {domain.host}, you need to add a TXT record to your DNS settings.
                  This process usually takes 5-30 minutes after DNS changes.
                </Typography>
              </Alert>

              {/* DNS Instructions */}
              {verificationData && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      Add This TXT Record
                    </Typography>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'background.level1', 
                      borderRadius: 'sm',
                      fontFamily: 'monospace',
                      mb: 2
                    }}>
                      <Typography level="body-sm">
                        <strong>Type:</strong> TXT<br />
                        <strong>Name:</strong> @<br />
                        <strong>Value:</strong> {(verificationData as any)?.domain?.verification_token}
                      </Typography>
                    </Box>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      Add this record in your DNS provider's control panel, then click "Verify Domain" below.
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Verification Status */}
              {verificationMessage && (
                <Alert sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon()}
                    <Typography level="body-sm">
                      {verificationMessage}
                    </Typography>
                  </Box>
                </Alert>
              )}

              {/* Current DNS Records */}
              {(verificationData as any)?.current_dns_records && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      Current DNS Records
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {Object.entries((verificationData as any).current_dns_records).map(([type, records]) => (
                        <Chip
                          key={type}
                          size="sm"
                          variant="outlined"
                          color={type === 'TXT' ? 'primary' : 'neutral'}
                        >
                          {type}: {Array.isArray(records) ? records.length : 0}
                        </Chip>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Verification Progress */}
              {isVerifying && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">
                      Verifying domain ownership...
                    </Typography>
                  </Box>
                  <LinearProgress />
                </Box>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRetry}
                  startDecorator={<RefreshIcon />}
                  disabled={isVerifying}
                >
                  Check Again
                </Button>
                
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleVerify}
                  loading={isVerifying}
                  disabled={verificationStatus === 'success'}
                >
                  {verificationStatus === 'success' ? 'Verified!' : 'Verify Domain'}
                </Button>
              </Box>

              {/* Help Text */}
              <Alert sx={{ mt: 3 }}>
                <Typography level="body-xs">
                  <strong>Need help?</strong> Check our{' '}
                  <Button
                    variant="plain"
                    size="sm"
                    onClick={() => window.open('https://help.apexos.com/dns-setup', '_blank')}
                  >
                    DNS setup guide
                  </Button>{' '}
                  or contact support if you're having trouble.
                </Typography>
              </Alert>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={onClose}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
